import { Component, ElementRef, Injector, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { GridBaseComponent } from '../../../../../components/common/grid-base/grid-base';
import { PolluxGridConfig } from '../../../../../components/common/pollux-grid-config/pollux-grid-config';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { PropertiesWorkspaceComponent } from '../../workspaces/properties-workspace/properties-workspace.component';
import { propertiesGridColumns } from './properties-grid-config';

/**
 * Properties grid
 */
@Component({
  selector: 'properties-grid',
  templateUrl: './properties-grid.component.html',
  styleUrls: ['./properties-grid.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PropertiesGridComponent extends GridBaseComponent implements OnInit {
  /**
   * A reference to the Kendo grid component
   */
  @ViewChild(GridComponent) kendoGrid: GridComponent;

  /**
   * A reference to the workspace
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Gets a list of columns
   * @returns
   */
  public getColumns(): PolluxGridConfig[] {
    return propertiesGridColumns;
  }

  /**
   * The workspace
   */
  @Input() workspace: PropertiesWorkspaceComponent;

  /**
   * Properties should be filtered by this ValuationPortfolioId
   */
  public filterByValuationPortfolioId: number = null;

  /**
   * Observable callback
   */
  private observeCallback = null;

  /**
   * Constructor
   * @param injector The Angular injector
   * @param elementRef ElementRef
   */
  constructor(protected injector: Injector, private elementRef: ElementRef) {
    super(injector, 'property', true);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.init();
  }

  /**
   * Handle a row selection / deselection event
   * @param _event The event. Can be null!
   * @param item The item. If an item was deselected, it's null
   */
  protected handleRowSelection(_event: any, item: any): void {
    this.eventBus.broadcastEvent('project-valuation-infobar-item-changed', {
      mode: 'property',
      item: item,
      itemIdentityProperty: 'Oid'
    });
  }

  /**
   * Handles if a cell was clicked
   * @param _event The event. Can be null!
   */
  protected handleCellClick(_event: any): void {
    //nothing
  }

  /**
   * Handle a doubleclick event
   * @param _event The event. Can be null!
   * @param item The item
   */
  protected handleDoubleClick(_event: any, item: any): void {
    const oidProperty = this.workspace.oidPropertyName;
    this.workspace.showForm(item[oidProperty]);
  }

  /**
   * Gets the url for loading a paged list
   * If server paging is not supported return null and override getDataUrl
   * @returns The url for loading a paged list
   */
  protected getPagedListEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Properties/list/paged?valEventId=${this.filterByValuationPortfolioId}`);
  }

  /**
   * Reset the filters and show all items
   */
  public showAll(): void {
    super.showAll();
    this.workspace.onShowAll();
  }

  /**
   * Data loading callback
   * @param data The loaded data
   */
  protected onDataLoaded(data: GridDataResult): void {
    if (this.filterByValuationPortfolioId) {
      //in workspace we requested only properties connected to portfolio (using OccursInValuationPortfolios), so all fetched are connected
      //remark: before we fetched also properties that were historically on project
      for (const item of data.data) {        
        item.assignedToSelectedPortfolio = true;
      }
    }

    this.workspace.processAssignedCalculationModelTemplates(data.data || []);

    this.workspace.setCounterValue('property', data.total);
  }

  /**
   * Registers any required events. Please don't forget to unsubscibe!
   */
  protected registerEvents(): void {
    const onEvents = this.onEvents;

    onEvents.push(this.eventBus.subscribeEvent('properties:getCheckedProperties', (callback, _options) => {
      const checkedProperties = this.getCheckedProperties();
      setTimeout(() => {
        callback(checkedProperties);
      });
    }));

    onEvents.push(this.eventBus.subscribeEvent('properties:observeCheckedProperties', (observeCallback, _options) => {
      this.observeCallback = observeCallback;
    }));
  }

  /**
   * A property can be assigned to multiple portfolios, but only to one project (actively, there is a project history). 
   * In order to assign or unassign a property to/from a valuatiion portfolio, a valuation portfolio or report must be selected in the according grid.
   * This is a prerequisite to load properties. If the user now checks the assignment checkbox (1st column), the property isn't assigned yet, so the property is assigned to that portfolio.
   * If it is already assigned to the valuation portfolio and the user clears the checkbox, it will not be assigned to that valuation portfolio any more.
   * 
   * @param dataItem The clicked property
   */
  public togglePropertyInPortfolio(dataItem: any): void {
    this.assignPropertiesToPortfolio([dataItem]);
    this.observeChecked();
  }

  /**
   * Selects or deselects the checkboxes
   */
  public selectOrDeselectAllCheckbox() {
    super.selectOrDeselectAllCheckbox();

    if (this.filterByValuationPortfolioId !== null) {
      for (const dataItem of this.currentPageItems) {
        dataItem.assignedToSelectedPortfolio = this.checkAllChecked;
      }

      this.assignPropertiesToPortfolio(this.currentPageItems);
    }

    setTimeout(() => {
      this.observeChecked();
    });
  }

  /**
   * Assigns properties to a portfolio
   * @param dataItems
   */
  private assignPropertiesToPortfolio(dataItems: any[]) {
    if (this.filterByValuationPortfolioId !== null) {
      const payloads = [];
      const getAssignToPortfolioPayload = (assign, propertyId, portfolioId) => {
        return {
          propertyId: propertyId,
          portfolioId: assign ? (portfolioId || 0) : -(portfolioId || 0),
          projectId: 0
        };
      };

      for (const dataItem of dataItems) {
        const assign: boolean = dataItem.assignedToSelectedPortfolio;
        const propertyId: number = dataItem.Oid;
        const portfolioId: number = this.filterByValuationPortfolioId;
        const payload = getAssignToPortfolioPayload(assign, propertyId, portfolioId);
        payloads.push(payload);
      }

      this.eventBus.broadcastEvent('property:assignPropertiesToPortolio', payloads);
    }
  }

  /**
   * Gets the checked properties
   * @returns
   */
  private getCheckedProperties(): any[] {
    const checkedProperties = [];
    const gridEl = this.elementRef.nativeElement as HTMLElement
    const checkboxes = Array.from(gridEl.querySelectorAll('.gridCheckboxProperty'));
    for (const checkbox of checkboxes) {
      if ((checkbox as HTMLInputElement).checked) {
        const propertyId = parseInt(checkbox.id.replace('gridCheckboxProperty', ''));
        checkedProperties.push(propertyId);
      }
    }

    return checkedProperties;
  }

  /**
   * Observe the checked state
   */
  private observeChecked(): void {
    if (this.observeCallback) {
      const checkedProperties = this.getCheckedProperties();
      setTimeout(() => {
        this.observeCallback(checkedProperties);
      });
    }
  }
}
